import React from 'react';
import { Nav } from '@components';
import { Eye, EyeOff } from 'react-feather';
import { useAuth } from '@hooks/useAuth';
import { LoaderContext } from '../../App';
import { LoaderOverlay } from '@components';
import toast from 'react-hot-toast';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

const Signup = () => {
  document.title = 'ProWebTrade - Signup';

  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    React.useState(false);
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  const [loadingCode, setLoadingCode] = React.useState(false);
  const [timeLeftCode, setTimeLeftCode] = React.useState(60);

  const [nameError, setNameError] = React.useState(null);
  const [emailError, setEmailError] = React.useState(null);
  const [phoneError, setPhoneError] = React.useState(null);
  const [codeError, setCodeError] = React.useState(null);
  const [passwordError, setPasswordError] = React.useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = React.useState(null);

  const [registerStatus, setRegisterStatus] = React.useState(null);

  const {
    checkUsername,
    checkEmail,
    sendVerification,
    verifyToken,
    checkPhone,
    signup,
    user,
    loading,
  } = useAuth();

  const { setLoading } = React.useContext(LoaderContext);
  React.useEffect(() => {
    setLoading(loading);
  }, [loading]);

  React.useEffect(() => {
    let timer;
    if (loadingCode) {
      timer = setInterval(() => {
        setTimeLeftCode((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setLoadingCode(false);
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [loadingCode]);

  if (user) {
    return window.location.replace('/dashboard/?view=dashboard');
  }

  const sendCode = async (e) => {
    setEmailError(null);

    if (!email || email.trim().length === 0) {
      return setEmailError('Email is required');
    } else if (!email.includes('@')) {
      return setEmailError('Email is invalid');
    }

    try {
      await sendVerification(email);
    } catch (err) {
      setEmailError(err.message);
      toast.error(err.message);

      return;
    }

    setLoadingCode(true);

    return toast.success('Code sent successfully');
  };

  const sign = async (e) => {
    e.preventDefault();

    let isError = false;

    setNameError(null);
    setEmailError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setCodeError(null);
    setPhoneError(null);

    // Validating name
    if (!name || name.trim().length === 0) {
      setNameError('Full Name is required');

      isError = true;
    }

    // Validating phone
    if (!phone || phone.trim().length < 7) {
      setPhoneError('Phone is required');

      isError = true;
    }

    // Validating email
    if (!email || email.trim().length === 0) {
      setEmailError('Email is required');

      isError = true;
    } else if (!email.includes('@')) {
      setEmailError('Email is invalid');

      isError = true;
    }

    // Validating confirm password
    if (!confirmPassword || confirmPassword.length === 0) {
      setConfirmPasswordError('Confirm password is required');
      isError = true;
    } else if (confirmPassword !== password) {
      setConfirmPasswordError('Passwords do not match');
      isError = true;
    }

    // Validating password
    if (!password || password.length === 0) {
      setPasswordError('Password is required');
      isError = true;
    } else if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      isError = true;
    } else if (!/\d/.test(password)) {
      setPasswordError('Password must contain at least one number');
      isError = true;
    } else if (!/[a-z]/.test(password)) {
      setPasswordError('Password must contain at least one lowercase letter');
      isError = true;
    } else if (!/[A-Z]/.test(password)) {
      setPasswordError('Password must contain at least one uppercase letter');
      isError = true;
    } else if (!/[^A-Za-z0-9]/.test(password)) {
      setPasswordError('Password must contain at least one symbol');
      isError = true;
    }

    if (code.length !== 4) {
      setCodeError('Code is invalid');
      isError = true;
    }

    // Checking if username is available
    const nameCheck = await checkUsername(name);
    if (nameCheck.error) {
      setNameError(nameCheck.error);
      isError = true;
    }

    // Checking if phone is available
    const phoneCheck = await checkPhone(phone);
    if (phoneCheck.error) {
      setPhoneError(phoneCheck.error);
      isError = true;
    }

    // Checking if email is available
    const emailCheck = await checkEmail(email);
    if (emailCheck.error) {
      setEmailError(emailCheck.error);
      isError = true;
    }

    try {
      await verifyToken({ email, code });
    } catch (err) {
      setCodeError(err.message);
      isError = true;
    }

    if (isError) {
      return null;
    }

    // If everything is valid, proceed with registration
    const data = await signup(name, email, password, code, phone);
    if (data?.error || data?.message)
      return setRegisterStatus(data?.error ? data.error : data.message);

    window.location.replace('/dashboard/?view=dashboard');
  };

  return (
    <section className="auth_page py-10">
      {loading && <LoaderOverlay />}
      <Nav hide="signup" />
      <main className="auth_container mt-10">
        <div className="auth_title">
          <h1 className="auth_title">Create a new account</h1>
        </div>
        <form className="auth_form">
          <div className="auth_form_group">
            <label htmlFor="email">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              onChange={(e) => setName(e.target.value)}
              value={name}
              placeholder="Enter your full name"
              className={nameError ? '!border-2 !border-red-700' : ''}
            />
            {nameError && <p className="error">{nameError}</p>}
          </div>
          <div className="auth_form_group">
            <label htmlFor="phone">Phone</label>
            <PhoneInput
              id="phone"
              name="phone"
              defaultCountry="ad"
              value={phone}
              onChange={(phone) => setPhone(phone)}
              className={'items-center w-full gap-2'}
              inputClassName={phoneError ? '!border-2 !border-red-700 !text-white !h-auto' : '!text-white !h-auto'}
            />
            {phoneError && <p className="error">{phoneError}</p>}
          </div>
          <div className="auth_form_group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              placeholder="Enter your email"
              className={emailError ? '!border-2 !border-red-700' : ''}
            />
            {emailError && <p className="error">{emailError}</p>}
          </div>
          <div className="auth_form_group">
            <label htmlFor="email">Confirm code</label>
            <div className="flex w-full align-center gap-2">
              <input
                type="text"
                id="code"
                name="code"
                onChange={(e) => setCode(e.target.value)}
                value={code}
                placeholder="Confirm code"
                className={`flex-1 ${codeError ? '!border-2 !border-red-700' : ''}`}
              />
              <button
                onClick={sendCode}
                type="button"
                disabled={loadingCode}
                className={`!w-[160px] !h-full !mt-0 ${loadingCode ? 'disabled_button' : ''}`}
              >
                {loadingCode ? `${timeLeftCode} s` : 'send code'}
              </button>
            </div>

            {codeError && <p className="error">{codeError}</p>}
          </div>
          <div className="auth_form_group">
            <label htmlFor="password">Password</label>
            <div className="password_input">
              <input
                type={passwordVisible ? 'text' : 'password'}
                autoCorrect="off"
                autoCapitalize="none"
                autoComplete="off"
                autoSave="off"
                id="password"
                name="password"
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                placeholder="Enter your password"
                className={passwordError ? '!border-2 !border-red-700' : ''}
              />
              <div className="eye_container">
                {passwordVisible ? (
                  <EyeOff
                    style={{ cursor: 'pointer' }}
                    color="rgba(119, 237, 145, .2)"
                    className="password_toggle"
                    onClick={() => setPasswordVisible(false)}
                  />
                ) : (
                  <Eye
                    style={{ cursor: 'pointer' }}
                    color="rgba(119, 237, 145, 1)"
                    className="password_toggle"
                    onClick={() => setPasswordVisible(true)}
                  />
                )}
              </div>
            </div>
            {passwordError && <p className="error">{passwordError}</p>}
          </div>
          <div className="auth_form_group">
            <label htmlFor="confirm_password">Confirm password</label>
            <div className="password_input">
              <input
                type={confirmPasswordVisible ? 'text' : 'password'}
                autoCorrect="off"
                autoCapitalize="none"
                autoComplete="off"
                autoSave="off"
                id="confirm_password"
                name="confirm_password"
                onChange={(e) => setConfirmPassword(e.target.value)}
                value={confirmPassword}
                placeholder="Confirm your password"
                className={
                  confirmPasswordError ? '!border-2 !border-red-700' : ''
                }
              />
              <div className="eye_container">
                {confirmPasswordVisible ? (
                  <EyeOff
                    style={{ cursor: 'pointer' }}
                    color="rgba(119, 237, 145, .2)"
                    className="password_toggle"
                    onClick={() => setConfirmPasswordVisible(false)}
                  />
                ) : (
                  <Eye
                    style={{ cursor: 'pointer' }}
                    color="rgba(119, 237, 145, 1)"
                    className="password_toggle"
                    onClick={() => setConfirmPasswordVisible(true)}
                  />
                )}
              </div>
            </div>
            {confirmPasswordError && (
              <p className="error">{confirmPasswordError}</p>
            )}
          </div>
          {registerStatus && <p className="error">{registerStatus}</p>}
          <button onClick={sign}>Sign up</button>
        </form>
      </main>
    </section>
  );
};

export default Signup;
